import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import confetti from "canvas-confetti";
import { CheckCircle2, Calendar, MapPin, Phone, Mail, User, ChevronLeft, ChevronRight } from "lucide-react";

const BookingConfirmation = () => {
  const { booking_id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/bookings/${booking_id}`
        );
        setBooking(response.data);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [booking_id]);

  useEffect(() => {
    if (!booking) return;
    const duration = 2 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };
    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) return clearInterval(interval);
      const particleCount = 50 * (timeLeft / duration);
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 250);

    return () => clearInterval(interval);
  }, [booking]);

  const formatDateTime = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString("en-IN", {
      weekday: "short",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-emerald-500 border-t-transparent" />
          <p className="mt-2 text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center bg-white rounded-xl shadow-sm border border-gray-100 p-8 max-w-md">
          <p className="text-lg font-semibold text-gray-800 mb-2">Booking not found</p>
          <p className="text-sm text-gray-500 mb-6">
            We couldn't find this booking. It may have been removed, or the link is incorrect.
          </p>
          <Link
            to="/"
            className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const images = (booking.decor_service_images || []).map(
    (img) => `${process.env.REACT_APP_BASE_URL}${img}`
  );
  const hasMultipleImages = images.length > 1;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10 md:py-14">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Success header */}
        <div className="bg-white rounded-2xl shadow-sm border-t-4 border-emerald-500 p-8 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Booking Confirmed!</h1>
          <p className="text-sm text-gray-500">
            Booking ID <span className="font-semibold text-gray-700">#{booking.id}</span> · we'll be in touch shortly to confirm the final details.
          </p>
        </div>

        {/* Service + schedule */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Service</p>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{booking.decor_service_name}</h2>

          {images.length > 0 && (
            <div className="mb-4">
              <div className="relative aspect-video overflow-hidden rounded-xl bg-gray-100 border border-gray-200">
                <img
                  src={images[currentImageIndex]}
                  alt={booking.decor_service_name}
                  className="h-full w-full object-cover object-center"
                />
                {hasMultipleImages && (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
                      }
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 p-1.5 rounded-full shadow-md hover:bg-white transition-all duration-200 border border-gray-200"
                    >
                      <ChevronLeft className="w-4 h-4 text-gray-700" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentImageIndex((prev) => (prev + 1) % images.length)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 p-1.5 rounded-full shadow-md hover:bg-white transition-all duration-200 border border-gray-200"
                    >
                      <ChevronRight className="w-4 h-4 text-gray-700" />
                    </button>
                    <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                      {currentImageIndex + 1} / {images.length}
                    </span>
                  </>
                )}
              </div>
              {hasMultipleImages && (
                <div className="mt-2 flex justify-center flex-wrap gap-2">
                  {images.map((img, index) => (
                    <button
                      type="button"
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                        currentImageIndex === index
                          ? "border-emerald-600 scale-105 shadow-sm"
                          : "border-gray-200 hover:border-emerald-400"
                      }`}
                    >
                      <img src={img} alt={`Service ${index + 1}`} className="h-full w-full object-cover object-center" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{formatDateTime(booking.start_datetime)}</span>
          </div>
        </div>

        {/* Customer details */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Customer Details</p>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <User className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{booking.customer_name}</span>
          </div>
          <div className="flex items-start gap-2 text-sm text-gray-700">
            <MapPin className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <span>{booking.address} — {booking.pincode}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Phone className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{booking.phone}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Mail className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>{booking.email}</span>
          </div>
        </div>

        {/* Price breakdown */}
        <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-6 shadow-sm">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Price Details</p>
          <div className="flex justify-between items-center text-sm text-gray-600 py-1">
            <span>Service price</span>
            <span className="font-medium text-gray-800">₹{booking.service_price}</span>
          </div>
          <div className="flex justify-between items-center text-sm text-gray-600 py-1">
            <span>
              Service Charges <span className="text-xs text-gray-400">({booking.pincode})</span>
            </span>
            <span className="font-medium text-gray-800">₹{booking.pincode_price}</span>
          </div>
          <div className="mt-2 pt-3 border-t border-dashed border-emerald-300 flex justify-between items-center">
            <span className="text-sm font-semibold text-gray-800">Total Price</span>
            <span className="text-2xl font-extrabold text-emerald-700">₹{booking.total_price}</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Link
            to="/decoration"
            className="flex-1 text-center bg-white border border-gray-200 hover:border-emerald-400 hover:text-emerald-700 text-gray-700 py-3 rounded-lg text-sm font-medium transition-colors"
          >
            Browse More Services
          </Link>
          <Link
            to="/"
            className="flex-1 text-center bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg text-sm font-medium transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
